import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Tab, Grid, Card, Icon, Modal, Form, Button, Message } from 'semantic-ui-react';

import Config from '../../../constants/Config';
import selectors from '../../../selectors';
import styles from './IntegrationsPane.module.scss';
import GoogleDriveModal from '../../CardModal/AttachmentAddZone/GoogleDriveModal';
import RcmWebhookConfig from './RcmWebhookConfig';
import RcmSyncConfig from './RcmSyncConfig';
import RcmEnrichmentToggles from './RcmEnrichmentToggles';
import RcmActivityLog from './RcmActivityLog';
import RcmDamageConfig from './RcmDamageConfig';

const IntegrationsPane = React.memo(() => {
  const [t] = useTranslation();
  const accessToken = useSelector(selectors.selectAccessToken);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [wheelbaseEmail, setWheelbaseEmail] = useState('');
  const [wheelbasePassword, setWheelbasePassword] = useState('');
  const [slackBotToken, setSlackBotToken] = useState('');
  const [slackChannels, setSlackChannels] = useState([{ id: '', name: '' }]);
  const [defaultSlackTestMessage, setDefaultSlackTestMessage] = useState('');
  const [selectedTestChannelId, setSelectedTestChannelId] = useState('');
  const [customTestMessage, setCustomTestMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [wheelbaseConfig, setWheelbaseConfig] = useState(null);
  const [slackConfig, setSlackConfig] = useState(null);
  const [googleDriveConfig, setGoogleDriveConfig] = useState(null);
  const [customerDetailsProgress, setCustomerDetailsProgress] = useState(null);
  const [vehicleDetailsProgress, setVehicleDetailsProgress] = useState(null);
  const [bookingDetailsProgress, setBookingDetailsProgress] = useState(null);
  const [isPollingProgress, setIsPollingProgress] = useState(false);
  const [isSyncingIncremental, setIsSyncingIncremental] = useState(false);
  const [isCreatingWeeklyCards, setIsCreatingWeeklyCards] = useState(false);
  const [isGoogleDriveKbModalOpen, setIsGoogleDriveKbModalOpen] = useState(false); // eslint-disable-line no-unused-vars
  const [isSyncingKb, setIsSyncingKb] = useState(false); // eslint-disable-line no-unused-vars

  // Rentalbuddy State
  const [rentalbuddyConfig, setRentalbuddyConfig] = useState(null);
  const [rentalbuddyDeliveries, setRentalbuddyDeliveries] = useState([]);
  const [isRotatingSecret, setIsRotatingSecret] = useState(false);
  const [showFullSecret, setShowFullSecret] = useState(false);
  const [rentalbuddyFirebaseUid, setRentalbuddyFirebaseUid] = useState('');
  const [isSavingFirebaseUid, setIsSavingFirebaseUid] = useState(false);
  const [rentalbuddyTargetProjectId, setRentalbuddyTargetProjectId] = useState('');
  const [rentalbuddyEnabledSteps, setRentalbuddyEnabledSteps] = useState([
    'Identity',
    'Drivers',
    'Coverage',
    'Addons',
    'Contract',
    'Videos',
    'Congrats',
    'Vehicle Location',
    'Bon Voyage',
    'Return Form',
    'Key Location',
    'Review',
    'Google Review',
  ]);
  const [isSettingUpBoard, setIsSettingUpBoard] = useState(false);

  const rentalbuddyAvailableSteps = [
    {
      group: 'Checking',
      steps: ['Identity', 'Drivers', 'Coverage', 'Addons', 'Contract', 'Videos', 'Congrats'],
    },
    {
      group: 'Pickup',
      steps: ['Vehicle Location', 'Bon Voyage'],
    },
    {
      group: 'DropOff',
      steps: ['Return Form', 'Key Location', 'Review', 'Google Review'],
    },
  ];

  // RCM State
  const [rcmVehiclesConfig, setRcmVehiclesConfig] = useState(null);
  const [rcmCompanyId, setRcmCompanyId] = useState('');

  const [rcmBookingsConfig, setRcmBookingsConfig] = useState(null);
  const [rcmBookingsApiKey, setRcmBookingsApiKey] = useState('');
  const [rcmBookingsSecret, setRcmBookingsSecret] = useState('');

  // Target location state
  const [targetProjectId, setTargetProjectId] = useState('');
  const [targetBoardId, setTargetBoardId] = useState('');
  const [targetListId, setTargetListId] = useState('');
  const [projects, setProjects] = useState([]);
  const [boards, setBoards] = useState([]); // eslint-disable-line no-unused-vars
  const [lists, setLists] = useState([]); // eslint-disable-line no-unused-vars

  const [rcmLocationId, setRcmLocationId] = useState(7);
  const [isAutoSyncing, setIsAutoSyncing] = useState(false);

  const applySlackConfigToState = useCallback(integration => {
    if (integration?.channels?.length) {
      const normalizedChannels = integration.channels.map(channel => ({
        id: channel.id || '',
        name: channel.name || '',
      }));

      setSlackChannels(normalizedChannels);
      setSelectedTestChannelId(integration.channelId || normalizedChannels[0].id || '');
    } else {
      setSlackChannels([{ id: '', name: '' }]);
      setSelectedTestChannelId('');
    }

    const message = integration?.defaultTestMessage || '';
    setDefaultSlackTestMessage(message);
    setCustomTestMessage(message);
  }, []);

  const handleSlackChannelChange = useCallback((index, field, value) => {
    setSlackChannels(prev =>
      prev.map((channel, idx) => (idx === index ? { ...channel, [field]: value } : channel))
    );
  }, []);

  const handleAddSlackChannel = useCallback(() => {
    setSlackChannels(prev => [...prev, { id: '', name: '' }]);
  }, []);

  const handleRemoveSlackChannel = useCallback(index => {
    setSlackChannels(prev => {
      if (prev.length === 1) {
        return [{ id: '', name: '' }];
      }

      return prev.filter((_, idx) => idx !== index);
    });
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await fetch(`${Config.SERVER_BASE_URL}/api/projects`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, [accessToken]);

  const fetchBoards = useCallback(
    async projectId => {
      if (!projectId) {
        setBoards([]);
        return;
      }
      try {
        const response = await fetch(`${Config.SERVER_BASE_URL}/api/projects/${projectId}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          // Assuming project endpoint returns included.boards or we need to access via relationships
          // If data structure matches previous assumption:
          const projectBoards = data.included?.boards || [];
          setBoards(projectBoards);
        } else {
          // Fallback to fetching all projects if individual fetch fails or structure is different
          const allResponse = await fetch(`${Config.SERVER_BASE_URL}/api/projects`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: 'include',
          });
          if (allResponse.ok) {
            const allData = await allResponse.json();
            const allBoards = allData.included?.boards || [];
            setBoards(allBoards.filter(b => b.projectId === projectId));
          }
        }
      } catch (error) {
        console.error('Error fetching boards:', error);
      }
    },
    [accessToken]
  ); // fetchBoards depends on accessToken

  const fetchLists = useCallback(
    async boardId => {
      if (!boardId) {
        setLists([]);
        return;
      }
      try {
        const response = await fetch(`${Config.SERVER_BASE_URL}/api/boards/${boardId}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          const lists = data.included?.lists || [];
          setLists(lists.sort((a, b) => a.position - b.position));
        }
      } catch (error) {
        console.error('Error fetching lists:', error);
      }
    },
    [accessToken]
  ); // fetchLists depends on accessToken

  const loadWheelbaseConfig = useCallback(async () => {
    try {
      console.log('🔍 Loading Wheelbase configuration...');

      const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/wheelbase/status`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      console.log('📥 Status response:', response.status);

      if (response.ok) {
        const statusData = await response.json();
        console.log('📊 Status data:', statusData);

        if (statusData.configured) {
          console.log('✅ Existing configuration found:', statusData.integration);
          setWheelbaseConfig(statusData.integration);
        } else {
          console.log('ℹ️ No Wheelbase configuration exists');
          setWheelbaseConfig(null);
        }
      } else {
        console.log('❌ Status request failed:', response.status);
        setWheelbaseConfig(null);
      }
    } catch (error) {
      console.log('❌ Error loading Wheelbase configuration:', error);
      setWheelbaseConfig(null);
    }
  }, [accessToken]);

  const loadSlackConfig = useCallback(async () => {
    try {
      console.log('🔍 Loading Slack configuration...');

      const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/slack/status`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        const statusData = await response.json();
        console.log('📊 Slack status data:', statusData);

        if (statusData.configured) {
          console.log('✅ Existing Slack configuration found:', statusData.integration);
          setSlackConfig(statusData.integration);
          applySlackConfigToState(statusData.integration);
        } else {
          console.log('ℹ️ No Slack configuration exists');
          setSlackConfig(null);
          applySlackConfigToState(null);
        }
      } else {
        console.log('❌ Slack status request failed:', response.status);
        setSlackConfig(null);
        applySlackConfigToState(null);
      }
    } catch (error) {
      console.log('❌ Error loading Slack configuration:', error);
      setSlackConfig(null);
      applySlackConfigToState(null);
    }
  }, [accessToken, applySlackConfigToState]);

  const loadGoogleDriveConfig = useCallback(async () => {
    try {
      console.log('🔍 Loading Google Drive configuration...');

      const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/google/status`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        const statusData = await response.json();
        console.log('📊 Google Drive status data:', statusData);

        if (statusData.isConnected) {
          console.log('✅ Google Drive connected:', statusData);
          setGoogleDriveConfig(statusData);
        } else {
          console.log('ℹ️ Google Drive not connected');
          setGoogleDriveConfig(null);
        }
      } else {
        console.log('❌ Google Drive status request failed:', response.status);
        setGoogleDriveConfig(null);
      }
    } catch (error) {
      console.log('❌ Error loading Google Drive configuration:', error);
      setGoogleDriveConfig(null);
    }
  }, [accessToken]);

  const loadRcmConfig = useCallback(async () => {
    try {
      console.log('🔍 Loading RCM configuration...');

      const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/rcm/status`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        const statusData = await response.json();
        console.log('📊 RCM status data:', statusData);

        // Handle Vehicles
        if (statusData.vehicles && statusData.vehicles.isConfigured) {
          setRcmVehiclesConfig(statusData.vehicles);
          setRcmLocationId(statusData.vehicles.currentLocationId || 7);
          setRcmCompanyId(statusData.vehicles.rcmCompanyId || '');
        } else {
          setRcmVehiclesConfig(null);
        }

        // Handle Bookings
        if (statusData.bookings && statusData.bookings.isConfigured) {
          setRcmBookingsConfig(statusData.bookings);
          setRcmBookingsApiKey(statusData.bookings.username || ''); // API Key stored in username field
          setTargetProjectId(statusData.bookings.targetProjectId || '');
          setTargetBoardId(statusData.bookings.targetBoardId || '');
          setTargetListId(statusData.bookings.targetListId || '');

          // Trigger fetch of boards and lists if data is present to pre-populate dropdowns
          if (statusData.bookings.targetProjectId) {
            fetchBoards(statusData.bookings.targetProjectId);
          }
          if (statusData.bookings.targetBoardId) {
            fetchLists(statusData.bookings.targetBoardId);
          }
        } else {
          setRcmBookingsConfig(null);
          // Only clear if not configured, otherwise keep state or default
          setTargetProjectId('');
          setTargetBoardId('');
          setTargetListId('');
        }
      } else {
        console.log('❌ RCM status request failed:', response.status);
        setRcmVehiclesConfig(null);
        setRcmBookingsConfig(null);
      }
    } catch (error) {
      console.log('❌ Error loading RCM configuration:', error);
      setRcmVehiclesConfig(null);
      setRcmBookingsConfig(null);
    }
  }, [accessToken, fetchBoards, fetchLists]);

  const loadRentalbuddyConfig = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/v1/integrations/rentalbuddy/status`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRentalbuddyConfig(data);
        if (data.firebaseUid) {
          setRentalbuddyFirebaseUid(data.firebaseUid);
        }
        if (data.targetProjectId) {
          setRentalbuddyTargetProjectId(data.targetProjectId);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load Rentalbuddy configuration');
      }
    } catch (err) {
      console.error('Error loading Rentalbuddy config:', err);
      setError('Network error loading Rentalbuddy configuration');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  const loadRentalbuddyDeliveries = useCallback(async () => {
    try {
      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/v1/integrations/rentalbuddy/deliveries`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRentalbuddyDeliveries(data.deliveries || []);
      }
    } catch (err) {
      console.error('Error loading Rentalbuddy deliveries:', err);
    }
  }, [accessToken]);

  const handleUpdateRentalbuddy = useCallback(
    async (isActive, rotateSecret = false) => {
      setIsLoading(true);
      if (rotateSecret) setIsRotatingSecret(true);

      try {
        const response = await fetch(
          `${Config.SERVER_BASE_URL}/api/v1/integrations/rentalbuddy/update`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              isActive,
              rotateSecret,
            }),
          }
        );

        if (response.ok) {
          const result = await response.json();
          setRentalbuddyConfig(prev => ({
            ...prev,
            isActive: result.isActive,
            webhookSecret: result.webhookSecret,
          }));

          if (result.rotated) {
            setSuccess('Secret rotated successfully! Make sure to update it in Rentalbuddy.');
            setShowFullSecret(true);
          } else {
            setSuccess(`Integration ${result.isActive ? 'enabled' : 'disabled'} successfully.`);
          }
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Failed to update Rentalbuddy settings');
        }
      } catch (err) {
        setError('Network error updating Rentalbuddy settings');
      } finally {
        setIsLoading(false);
        setIsRotatingSecret(false);
      }
    },
    [accessToken]
  );

  const handleSaveFirebaseUid = useCallback(async () => {
    setIsSavingFirebaseUid(true);
    try {
      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/v1/integrations/rentalbuddy/update`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ firebaseUid: rentalbuddyFirebaseUid.trim() }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        setRentalbuddyConfig(prev => ({
          ...prev,
          firebaseUid: result.firebaseUid,
        }));
        setSuccess('Owner ID saved successfully.');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save Owner ID');
      }
    } catch (err) {
      setError('Network error saving Owner ID');
    } finally {
      setIsSavingFirebaseUid(false);
    }
  }, [accessToken, rentalbuddyFirebaseUid]);

  const handleCreateRentalbuddyBoard = useCallback(async () => {
    if (!rentalbuddyTargetProjectId) {
      setError('Please select a project first.');
      return;
    }

    setIsSettingUpBoard(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/v1/integrations/rentalbuddy/setup-board`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            projectId: rentalbuddyTargetProjectId,
            enabledSteps: rentalbuddyEnabledSteps,
          }),
        }
      );

      if (response.ok) {
        setSuccess('Rental Buddy board and lists created successfully!');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create Rental Buddy board');
      }
    } catch (err) {
      setError('Network error creating Rental Buddy board');
    } finally {
      setIsSettingUpBoard(false);
    }
  }, [accessToken, rentalbuddyTargetProjectId, rentalbuddyEnabledSteps]);

  const toggleRentalbuddyStep = useCallback(step => {
    setRentalbuddyEnabledSteps(prev =>
      prev.includes(step) ? prev.filter(s => s !== step) : [...prev, step]
    );
  }, []);

  // Load existing configuration on component mount
  useEffect(() => {
    if (accessToken) {
      loadWheelbaseConfig();
      loadSlackConfig();
      loadGoogleDriveConfig();
      loadRcmConfig();
      loadRentalbuddyConfig();
    }
  }, [
    loadWheelbaseConfig,
    loadSlackConfig,
    loadGoogleDriveConfig,
    loadRcmConfig,
    loadRentalbuddyConfig,
    accessToken,
  ]);

  // Poll for vehicle and booking details progress
  useEffect(() => {
    let pollInterval;

    const pollProgress = async () => {
      if (!isPollingProgress || !accessToken) {
        return;
      }

      try {
        // Poll all progress types
        const response = await fetch(
          `${Config.SERVER_BASE_URL}/api/integrations/wheelbase/sync-progress?syncType=all`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: 'include',
          }
        );

        if (response.ok) {
          const progressData = await response.json();

          if (progressData.success && progressData.progress && progressData.progress.length > 0) {
            let customersDone = !customerDetailsProgress; // true if not tracking customers
            let vehiclesDone = !vehicleDetailsProgress; // true if not tracking vehicles
            let bookingsDone = !bookingDetailsProgress; // true if not tracking bookings

            // Process each progress record
            for (const progress of progressData.progress) {
              if (progress.syncType === 'customer_details' && customerDetailsProgress) {
                console.log('📊 Customer details progress:', progress);

                // Only update if status changed
                const previousStatus = customerDetailsProgress.status;

                setCustomerDetailsProgress({
                  ...progress,
                  percentage: progress.percentage || 0,
                });

                // Only show completion message once when status changes to completed
                if (progress.status === 'completed' && previousStatus !== 'completed') {
                  setSuccess(
                    prev =>
                      `${prev} Customer details sync completed! ${progress.processedRecords}/${progress.totalRecords} customers synced.`
                  );
                  console.log('✅ Customer details sync completed');
                } else if (progress.status === 'failed' && previousStatus !== 'failed') {
                  setError(
                    prev => `${prev || ''} Customer details sync failed: ${progress.errorMessage}`
                  );
                  console.log('❌ Customer details sync failed');
                }

                // Check if this sync is done (completed or failed)
                customersDone = progress.status === 'completed' || progress.status === 'failed';
              } else if (progress.syncType === 'vehicle_details' && vehicleDetailsProgress) {
                console.log('📊 Vehicle details progress:', progress);

                // Only update if status changed
                const previousStatus = vehicleDetailsProgress.status;

                setVehicleDetailsProgress({
                  ...progress,
                  percentage: progress.percentage || 0,
                });

                // Only show completion message once when status changes to completed
                if (progress.status === 'completed' && previousStatus !== 'completed') {
                  setSuccess(
                    prev =>
                      `${prev} Vehicle details sync completed! ${progress.processedRecords}/${progress.totalRecords} vehicles synced.`
                  );
                  console.log('✅ Vehicle details sync completed');
                } else if (progress.status === 'failed' && previousStatus !== 'failed') {
                  setError(
                    prev => `${prev || ''} Vehicle details sync failed: ${progress.errorMessage}`
                  );
                  console.log('❌ Vehicle details sync failed');
                }

                // Check if this sync is done (completed or failed)
                vehiclesDone = progress.status === 'completed' || progress.status === 'failed';
              } else if (progress.syncType === 'booking_details' && bookingDetailsProgress) {
                console.log('📊 Booking details progress:', progress);

                // Only update if status changed
                const previousStatus = bookingDetailsProgress.status;

                setBookingDetailsProgress({
                  ...progress,
                  percentage: progress.percentage || 0,
                });

                // Only show completion message once when status changes to completed
                if (progress.status === 'completed' && previousStatus !== 'completed') {
                  setSuccess(
                    prev =>
                      `${prev} Booking details sync completed! ${progress.processedRecords}/${progress.totalRecords} bookings synced.`
                  );
                  console.log('✅ Booking details sync completed');
                } else if (progress.status === 'failed' && previousStatus !== 'failed') {
                  setError(
                    prev => `${prev || ''} Booking details sync failed: ${progress.errorMessage}`
                  );
                  console.log('❌ Booking details sync failed');
                }

                // Check if this sync is done (completed or failed)
                bookingsDone = progress.status === 'completed' || progress.status === 'failed';
              }
            }

            // Stop polling if all tracked syncs are done
            if (customersDone && vehiclesDone && bookingsDone) {
              console.log('🛑 All syncs completed, stopping polling');
              setIsPollingProgress(false);
            }
          }
        }
      } catch (error) {
        console.error('Error polling progress:', error);
      }
    };

    if (isPollingProgress) {
      // Poll immediately
      pollProgress();

      // Then poll every 3 seconds
      pollInterval = setInterval(pollProgress, 3000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [
    isPollingProgress,
    accessToken,
    customerDetailsProgress,
    vehicleDetailsProgress,
    bookingDetailsProgress,
  ]);

  // Effect to load projects when RCM Bookings integration is selected
  useEffect(() => {
    if (selectedIntegration?.id === 'rcm_bookings') {
      fetchProjects();
    }
  }, [selectedIntegration, fetchProjects]);

  // Effect to load boards when project changes
  useEffect(() => {
    if (targetProjectId) {
      fetchBoards(targetProjectId);
    } else {
      setBoards([]);
    }
  }, [targetProjectId, fetchBoards]); // Dependencies are correct now

  // Effect to load lists when board changes
  useEffect(() => {
    if (targetBoardId) {
      fetchLists(targetBoardId);
    } else {
      setLists([]);
    }
  }, [targetBoardId, fetchLists]); // Dependencies are correct now

  const handleIntegrationClick = useCallback(
    integration => {
      console.log('🎯 Opening integration modal:', integration.id);
      console.log(
        '🔍 Before clearing - wheelbaseEmail:',
        wheelbaseEmail,
        'wheelbasePassword:',
        wheelbasePassword
      );

      setSelectedIntegration(integration);

      // Clear fields when opening modal (without setTimeout)
      console.log('🧹 Clearing fields...');
      setWheelbaseEmail('');
      setWheelbasePassword('');
      setError(null);
      setSuccess(null);

      console.log('🔍 After clearing - wheelbaseEmail should be empty');

      // Reload configuration when opening modals
      if (integration.id === 'wheelbase') {
        loadWheelbaseConfig();
      } else if (integration.id === 'slack') {
        loadSlackConfig();
        setSlackBotToken('');
        applySlackConfigToState(slackConfig);
      } else if (integration.id === 'google-drive') {
        loadGoogleDriveConfig();
      } else if (integration.id === 'rcm_vehicles' || integration.id === 'rcm_bookings') {
        loadRcmConfig();
      } else if (integration.id === 'rentalbuddy') {
        loadRentalbuddyConfig();
        loadRentalbuddyDeliveries();
        fetchProjects();
        setShowFullSecret(false);
      }
    },
    [
      loadWheelbaseConfig,
      loadSlackConfig,
      loadGoogleDriveConfig,
      loadRcmConfig,
      loadRentalbuddyConfig,
      loadRentalbuddyDeliveries,
      wheelbaseEmail,
      wheelbasePassword,
      applySlackConfigToState,
      slackConfig,
      fetchProjects,
    ]
  );

  const handleCloseModal = useCallback(() => {
    setSelectedIntegration(null);
    setWheelbaseEmail('');
    setWheelbasePassword('');
    setSlackBotToken('');
    setRcmBookingsApiKey('');
    setRcmBookingsSecret('');
    setRcmLocationId(7);
    setRentalbuddyFirebaseUid('');
    setRentalbuddyTargetProjectId('');
    setRentalbuddyEnabledSteps([
      'Identity',
      'Drivers',
      'Coverage',
      'Addons',
      'Contract',
      'Videos',
      'Congrats',
      'Vehicle Location',
      'Bon Voyage',
      'Return Form',
      'Key Location',
      'Review',
      'Google Review',
    ]);
    // DO NOT reset target location here to persist selection while modal is closed but pane is active
    // If you want to reset ONLY when switching organizations or logging out, do it in useEffect(accessToken)
    // setTargetProjectId('');
    // setTargetBoardId('');
    // setTargetListId('');
    // setProjects([]);
    // setBoards([]);
    // setLists([]);
    applySlackConfigToState(slackConfig);
    setError(null);
    setSuccess(null);
  }, [applySlackConfigToState, slackConfig]);

  const handleRcmConfigure = useCallback(
    async (type, overrides = {}) => {
      let payload = {
        type,
        currentLocationId: rcmLocationId,
      };

      if (type === 'rcm_vehicles') {
        if (rcmCompanyId.trim()) {
          payload.rcmCompanyId = rcmCompanyId.trim();
        }
      } else if (type === 'rcm_bookings') {
        if (!rcmBookingsConfig && (!rcmBookingsApiKey.trim() || !rcmBookingsSecret.trim())) {
          setError('Please enter both API Key and Shared Secret');
          return;
        }
        payload.apiKey = rcmBookingsApiKey.trim();
        payload.sharedSecret = rcmBookingsSecret;

        // Include target location
        payload.targetProjectId = targetProjectId;
        payload.targetBoardId = targetBoardId;
        payload.targetListId = targetListId;

        // Merge any additional overrides (from sub-components)
        Object.assign(payload, overrides);
      }

      setIsLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/rcm/configure`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Configuration failed (${response.status})`);
        }

        const result = await response.json();
        setSuccess(
          `RCM ${type === 'rcm_vehicles' ? 'Vehicles' : 'Bookings'} configured successfully!`
        );

        if (type === 'rcm_vehicles') {
          setRcmVehiclesConfig({ ...result, isConfigured: true });
        } else {
          const savedSettings = result.item?.settings || result.settings || {};
          setRcmBookingsConfig({
            ...result,
            isConfigured: true,
            username: result.username,
            settings: savedSettings,
          });
          setRcmBookingsSecret('');
          setTargetProjectId(savedSettings.targetProjectId || result.targetProjectId || '');
          setTargetBoardId(savedSettings.targetBoardId || result.targetBoardId || '');
          setTargetListId(savedSettings.targetListId || result.targetListId || '');
        }
      } catch (error) {
        console.error('❌ Error configuring RCM:', error);
        setError(error.message || 'Failed to configure RCM integration');
      } finally {
        setIsLoading(false);
      }
    },
    [
      rcmCompanyId,
      rcmBookingsApiKey,
      rcmBookingsSecret,
      rcmLocationId,
      accessToken,
      targetProjectId,
      targetBoardId,
      targetListId,
      rcmBookingsConfig,
    ]
  );

  const handleRcmBookingsConfigUpdate = useCallback(
    overrides => {
      handleRcmConfigure('rcm_bookings', overrides);
    },
    [handleRcmConfigure]
  );

  const handleRcmDelete = useCallback(
    async type => {
      if (
        !window.confirm(
          `Are you sure you want to delete the RCM ${
            type === 'rcm_vehicles' ? 'Vehicles' : 'Bookings'
          } integration?`
        )
      ) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `${Config.SERVER_BASE_URL}/api/integrations/rcm?type=${type}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: 'include',
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to delete integration (${response.status})`);
        }

        setSuccess(
          `RCM ${
            type === 'rcm_vehicles' ? 'Vehicles' : 'Bookings'
          } integration deleted successfully`
        );

        if (type === 'rcm_vehicles') {
          setRcmVehiclesConfig(null);
          setRcmLocationId(7);
          setRcmCompanyId('');
        } else {
          setRcmBookingsConfig(null);
          setRcmBookingsApiKey('');
          setRcmBookingsSecret('');
        }
      } catch (error) {
        console.error('❌ Error deleting RCM integration:', error);
        setError(error.message || 'Failed to delete integration');
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken]
  );

  const handleRcmSyncFleet = useCallback(async () => {
    setIsSyncing(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('🔄 Starting RCM fleet synchronization...');

      const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/rcm/sync-fleet`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        let errorMessage = `Sync failed (${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Response is not JSON (e.g. plain text "Bad Request")
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ RCM Sync successful:', result);

      const vehicleStats = result.stats?.vehicles || {};
      const locationStats = result.stats?.locations || {};
      setSuccess(
        `Fleet synchronized successfully! Vehicles: ${vehicleStats.created || 0} created, ${
          vehicleStats.updated || 0
        } updated. Locations: ${locationStats.created || 0} created, ${
          locationStats.updated || 0
        } updated.`
      );
    } catch (error) {
      console.error('❌ RCM sync error:', error);
      setError(error.message || 'Failed to synchronize fleet');
    } finally {
      setIsSyncing(false);
    }
  }, [accessToken]);

  const handleRcmAutoSyncNow = useCallback(async () => {
    setIsAutoSyncing(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/integrations/rcm/bookings/sync-manual`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to trigger auto-sync');
      }

      const result = await response.json();
      setSuccess(result.message || 'Auto-sync process finished successfully!');
    } catch (error) {
      console.error('❌ Error triggering RCM auto-sync:', error);
      setError(error.message || 'Failed to trigger auto-sync');
    } finally {
      setIsAutoSyncing(false);
    }
  }, [accessToken]);

  const handleSaveCredentials = useCallback(async () => {
    if (!wheelbaseEmail.trim() || !wheelbasePassword.trim()) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('💾 Saving Wheelbase credentials:', { email: wheelbaseEmail });

      // Configure Wheelbase integration (save credentials only)
      const configResponse = await fetch(
        `${Config.SERVER_BASE_URL}/api/integrations/wheelbase/configure`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: JSON.stringify({
            email: wheelbaseEmail.trim(),
            password: wheelbasePassword,
          }),
        }
      );

      if (!configResponse.ok) {
        const errorData = await configResponse.json();
        throw new Error(errorData.message || `Configuration failed (${configResponse.status})`);
      }

      const configResult = await configResponse.json();
      console.log('✅ Credentials saved:', configResult);

      setSuccess(
        `Wheelbase credentials saved successfully! (${configResult.userType}: ${configResult.wheelbaseEmail})`
      );
      setWheelbaseConfig(configResult);

      // Clear both fields for security and UX
      setWheelbaseEmail('');
      setWheelbasePassword('');
    } catch (error) {
      console.error('❌ Error saving Wheelbase credentials:', error);
      setError(error.message || 'Failed to save Wheelbase credentials');
    } finally {
      setIsLoading(false);
    }
  }, [wheelbaseEmail, wheelbasePassword, accessToken]);

  const handleSyncData = useCallback(async () => {
    setIsSyncing(true);
    setError(null);
    setSuccess(null);
    setCustomerDetailsProgress(null);
    setVehicleDetailsProgress(null);
    setBookingDetailsProgress(null);

    try {
      console.log('🔄 Starting Wheelbase data synchronization...');

      const syncResponse = await fetch(
        `${Config.SERVER_BASE_URL}/api/integrations/wheelbase/sync`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: JSON.stringify({
            dataTypes: [
              'customers',
              'vehicles',
              'services',
              'bookings',
              'reviews',
              'addons',
              'conversations',
            ],
            force: true, // Para testing - bypass rate limit
            includeVehicleDetails: true, // Always include detailed vehicle sync
          }),
        }
      );

      if (!syncResponse.ok) {
        const errorData = await syncResponse.json();
        throw new Error(errorData.message || `Sync failed (${syncResponse.status})`);
      }

      const syncResult = await syncResponse.json();
      console.log('✅ Sync successful:', syncResult);

      // Mensaje simple con solo el total de registros sincronizados
      const successMessage = `Data synchronized successfully! ${syncResult.totalRecords} records synced.`;

      setSuccess(successMessage);
    } catch (error) {
      console.error('❌ Wheelbase sync error:', error);
      setError(error.message || 'Failed to synchronize Wheelbase data');
    } finally {
      setIsSyncing(false);
    }
  }, [accessToken]);

  const handleSyncIncremental = useCallback(async () => {
    setIsSyncingIncremental(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('🔄 Starting incremental sync...');

      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/integrations/wheelbase/sync-incremental`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Sync failed (${response.status})`);
      }

      const result = await response.json();
      console.log('✅ Incremental sync result:', result);

      if (result.success) {
        if (result.skipped) {
          setSuccess('No changes detected - all bookings up to date');
        } else {
          setSuccess(
            `${result.queued || 0} bookings added to queue. Worker will process them shortly.`
          );
        }
      } else {
        throw new Error(result.error || 'Sync failed');
      }
    } catch (error) {
      console.error('❌ Error in incremental sync:', error);
      setError(error.message || 'Failed to sync');
    } finally {
      setIsSyncingIncremental(false);
    }
  }, [accessToken]);

  const handleCreateWeeklyCards = useCallback(async () => {
    setIsCreatingWeeklyCards(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('📅 Creating weekly pickup/dropoff cards...');

      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/integrations/wheelbase/create-weekly-cards`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed (${response.status})`);
      }

      const result = await response.json();
      console.log('✅ Weekly cards result:', result);

      if (result.success) {
        setSuccess(
          `Weekly cards created! ${result.created} created, ${result.updated} updated. ${result.pickups} pickups, ${result.dropoffs} dropoffs.`
        );
      } else {
        throw new Error(result.error || 'Failed to create weekly cards');
      }
    } catch (error) {
      console.error('❌ Error creating weekly cards:', error);
      setError(error.message || 'Failed to create weekly cards');
    } finally {
      setIsCreatingWeeklyCards(false);
    }
  }, [accessToken]);

  const [isDeletingKbFile, setIsDeletingKbFile] = useState(false); // eslint-disable-line no-unused-vars

  // Legacy KB handlers — kept for potential rollback but currently unused
  // eslint-disable-next-line no-unused-vars
  const handleSyncKb = useCallback(
    async files => {
      setIsSyncingKb(true);
      setError(null);
      setSuccess(null);
      try {
        // Ensure files is an array (single select might return object if bug, but we forced array in modal)
        const fileList = Array.isArray(files) ? files : [files];

        console.log('📚 Syncing Knowledge Base...', fileList);
        const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/google/sync-kb`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            files: fileList.map(f => ({ id: f.id, name: f.name, mimeType: f.mimeType })),
          }),
        });

        if (!response.ok) {
          let errorMessage = 'Failed to sync Knowledge Base';
          try {
            const errData = await response.json();
            errorMessage = errData.message || errorMessage;
          } catch (e) {
            // If response is not JSON (e.g. 504 Gateway Timeout HTML), use status text
            errorMessage = `Server Error: ${response.status} ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();
        setSuccess(`Knowledge Base updated! ${result.syncedCount} files indexed.`);
        loadGoogleDriveConfig(); // Reload to show new list
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setIsSyncingKb(false);
      }
    },
    [accessToken, loadGoogleDriveConfig]
  );

  // eslint-disable-next-line no-unused-vars
  const handleDeleteKbFile = useCallback(
    async fileId => {
      if (!window.confirm('Are you sure you want to remove this file from the Knowledge Base?'))
        return;

      setIsDeletingKbFile(true);
      try {
        const response = await fetch(
          `${Config.SERVER_BASE_URL}/api/integrations/google/kb-files/${fileId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to delete file');
        }

        const result = await response.json();
        setGoogleDriveConfig(prev => ({
          ...prev,
          syncedFiles: result.remainingFiles,
        }));
        setSuccess('File removed from Knowledge Base');
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setIsDeletingKbFile(false);
      }
    },
    [accessToken]
  );

  const handleDeleteIntegration = useCallback(async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete the Wheelbase integration? This will PERMANENTLY remove all saved credentials and synced data (customers, vehicles, bookings, etc.).'
      )
    ) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('🗑️ Deleting Wheelbase integration and all synced data...');

      const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/wheelbase`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Delete failed (${response.status})`);
      }

      const result = await response.json();
      console.log('✅ Delete successful:', result);

      // Clear local state after successful deletion
      setWheelbaseConfig(null);
      setWheelbaseEmail('');
      setWheelbasePassword('');

      setSuccess(
        `Wheelbase integration deleted successfully! Removed ${result.totalDeleted} records including credentials, customers, vehicles, bookings, and all synced data.`
      );
    } catch (error) {
      console.error('❌ Error deleting integration:', error);
      setError(error.message || 'Failed to delete Wheelbase integration');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  // Slack functions
  const handleSaveSlackCredentials = useCallback(async () => {
    const sanitizedChannels = slackChannels
      .map(channel => ({
        id: (channel.id || '').trim(),
        name: (channel.name || '').trim(),
      }))
      .filter(channel => channel.id);

    if (!sanitizedChannels.length) {
      setError('Please add at least one Slack channel.');
      return;
    }

    if (!slackConfig && !slackBotToken.trim()) {
      setError('Please enter your Bot Token to connect Slack.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('💾 Saving Slack credentials...');

      const payload = {
        channels: sanitizedChannels,
        defaultTestMessage: defaultSlackTestMessage.trim(),
        channelId: sanitizedChannels[0].id,
      };

      if (slackBotToken.trim()) {
        payload.botToken = slackBotToken.trim();
      }

      const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/slack/configure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Configuration failed (${response.status})`);
      }

      const result = await response.json();
      console.log('✅ Slack configured:', result);

      setSuccess(`Slack connected successfully! Workspace: ${result.config.teamName}`);
      setSlackConfig(result.config);
      setSlackBotToken('');
      applySlackConfigToState(result.config);
      loadSlackConfig();
    } catch (error) {
      console.error('❌ Error saving Slack credentials:', error);
      setError(error.message || 'Failed to save Slack credentials');
    } finally {
      setIsLoading(false);
    }
  }, [
    slackBotToken,
    slackChannels,
    defaultSlackTestMessage,
    accessToken,
    loadSlackConfig,
    applySlackConfigToState,
    slackConfig,
  ]);

  const handleTestSlack = useCallback(async () => {
    if (!slackConfig) {
      setError('Slack is not configured yet.');
      return;
    }

    setIsTesting(true);
    setError(null);
    setSuccess(null);

    const availableChannels = (
      slackConfig.channels && slackConfig.channels.length > 0 ? slackConfig.channels : slackChannels
    ).filter(channel => channel.id && channel.id.trim());

    const fallbackChannelId =
      selectedTestChannelId || slackConfig.channelId || availableChannels[0]?.id || '';

    if (!fallbackChannelId) {
      setError('Please select a Slack channel before sending the test message.');
      setIsTesting(false);
      return;
    }

    const trimmedMessage = (
      customTestMessage ||
      defaultSlackTestMessage ||
      '🧪 Test message from Shakkii'
    ).trim();

    try {
      console.log('🧪 Testing Slack connection...');

      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/integrations/slack/test-connection`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: JSON.stringify({
            channelId: fallbackChannelId,
            message: trimmedMessage,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Test failed (${response.status})`);
      }

      const result = await response.json();
      console.log('✅ Test message sent:', result);

      setSuccess(`Test message sent to Slack channel ${fallbackChannelId}.`);
    } catch (error) {
      console.error('❌ Error testing Slack:', error);
      setError(error.message || 'Failed to send test message');
    } finally {
      setIsTesting(false);
    }
  }, [
    accessToken,
    slackConfig,
    slackChannels,
    selectedTestChannelId,
    customTestMessage,
    defaultSlackTestMessage,
  ]);

  const handleDisconnectSlack = useCallback(async () => {
    if (
      !window.confirm(
        'Are you sure you want to disconnect Slack? This will remove all saved credentials.'
      )
    ) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('🗑️ Disconnecting Slack...');

      const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/slack`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Disconnect failed (${response.status})`);
      }

      const result = await response.json();
      console.log('✅ Slack disconnected:', result);

      setSlackConfig(null);
      applySlackConfigToState(null);
      setSuccess('Slack disconnected successfully!');
    } catch (error) {
      console.error('❌ Error disconnecting Slack:', error);
      setError(error.message || 'Failed to disconnect Slack');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, applySlackConfigToState]);

  const handleConnectGoogle = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('🔗 Getting Google Auth URL...');
      const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/google/auth-url`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get auth URL');
      }

      const data = await response.json();
      if (data.url) {
        console.log('🔗 Redirecting to:', data.url);
        window.location.href = data.url;
      } else {
        throw new Error('No redirect URL returned');
      }
    } catch (error) {
      console.error('❌ Error starting Google Auth:', error);
      setError(error.message || 'Failed to start Google authentication');
      setIsLoading(false);
    }
  }, [accessToken]);

  const handleDisconnectGoogle = useCallback(async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Drive?')) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${Config.SERVER_BASE_URL}/api/integrations/google`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect Google Drive');
      }

      setGoogleDriveConfig(null);
      setSuccess('Google Drive disconnected successfully');
    } catch (error) {
      console.error('❌ Error disconnecting Google Drive:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  const testChannelOptions = useMemo(() => {
    const sourceChannels =
      slackConfig?.channels && slackConfig.channels.length > 0
        ? slackConfig.channels
        : slackChannels;

    return sourceChannels
      .filter(channel => channel && channel.id && channel.id.trim())
      .map(channel => ({
        key: channel.id,
        value: channel.id,
        text: channel.name ? `${channel.name} (${channel.id})` : channel.id,
      }));
  }, [slackConfig, slackChannels]);

  useEffect(() => {
    if (!testChannelOptions.length) {
      if (selectedTestChannelId) {
        setSelectedTestChannelId('');
      }
      return;
    }

    const currentExists = testChannelOptions.some(option => option.value === selectedTestChannelId);

    if (!currentExists) {
      setSelectedTestChannelId(testChannelOptions[0].value);
    }
  }, [testChannelOptions, selectedTestChannelId]);

  const hasValidSlackChannel = slackChannels.some(channel => (channel.id || '').trim().length > 0);
  const requiresBotToken = !slackConfig;
  const slackSaveDisabled =
    isLoading || !hasValidSlackChannel || (requiresBotToken && !slackBotToken.trim());

  const integrations = [
    // 1. Wheelbase - FUNCIONAL
    {
      id: 'wheelbase',
      name: 'Wheelbase',
      description: 'Fleet management and vehicle tracking',
      logo: '/Wheelbase-logo.jpeg',
      color: '#1E88E5',
      status: 'available',
    },
    // 2. ChatGPT - AVAILABLE
    {
      id: 'chatgpt',
      name: 'ChatGPT',
      description: 'AI-powered assistant for automated workflows and support',
      logo: '/chatgpt-logo.jpg',
      color: '#10A37F',
      status: 'available',
    },
    // 3. Google Drive - AVAILABLE
    {
      id: 'google-drive',
      name: 'Google Drive',
      description: 'Cloud storage and file sharing integration',
      logo: '/Google_Drive_logo.png',
      color: '#4285F4',
      status: 'available',
    },
    // 4. Slack - AVAILABLE
    {
      id: 'slack',
      name: 'Slack',
      description: 'Team communication and collaboration platform',
      logo: '/slack_logo.png',
      color: '#4A154B',
      status: 'available',
    },
    // 5. n8n - AVAILABLE
    {
      id: 'n8n',
      name: 'n8n',
      description: 'Workflow automation and integration platform',
      logo: '/N8N-badge.png',
      color: '#FF6D5A',
      status: 'available',
    },
    // 6. RCM Vehicles - AVAILABLE
    {
      id: 'rcm_vehicles',
      name: 'RCM Vehicles',
      description: 'Sync fleet status and mileage from RCM',
      logo: '/RCMLOGO.png',
      color: '#FF6B35',
      status: 'available',
    },
    // 7. RCM Bookings - AVAILABLE
    {
      id: 'rcm_bookings',
      name: 'RCM Bookings',
      description: 'Fetch booking data via RCM Export API',
      logo: '/RCMLOGO.png',
      color: '#E65100', // Slightly darker orange
      status: 'available',
    },
    // 8. Rentalbuddy - AVAILABLE
    {
      id: 'rentalbuddy',
      name: 'Rentalbuddy',
      description: 'Sync bookings and vehicle data via Firebase Webhooks',
      logo: '/rentalbuddy logo orange.png',
      color: '#F57C00',
      status: 'available',
    },
  ];

  const renderIntegrationTile = integration => (
    <Grid.Column key={integration.id} mobile={16} tablet={8} computer={5}>
      <Card
        className={`${styles.integrationCard} ${
          integration.status === 'coming-soon' && !integration.isFeatured ? styles.disabled : ''
        } ${integration.isFeatured ? styles.featured : ''}`}
        onClick={() =>
          (integration.status === 'available' || integration.isFeatured) &&
          handleIntegrationClick(integration)
        }
      >
        {/* Status badge in top-right corner */}
        <div
          className={`${styles.statusBadge} ${
            integration.status === 'coming-soon' ? styles.comingSoon : styles.available
          }`}
        >
          <Icon
            name={integration.status === 'available' ? 'check circle' : 'clock outline'}
            size="small"
          />
          {integration.status === 'available' ? 'Available' : 'Coming Soon'}
        </div>

        <Card.Content className={styles.cardContent}>
          <div className={styles.iconContainer}>
            {integration.logo ? (
              <img
                src={integration.logo}
                alt={`${integration.name} logo`}
                className={styles.integrationLogo}
              />
            ) : (
              <div
                className={styles.integrationIcon}
                style={{ backgroundColor: integration.color }}
              >
                {integration.icon}
              </div>
            )}
          </div>
          <Card.Header className={styles.integrationName}>{integration.name}</Card.Header>
          <Card.Description className={styles.integrationDescription}>
            {integration.description}
          </Card.Description>
        </Card.Content>
      </Card>
    </Grid.Column>
  );

  return (
    <Tab.Pane attached={false} className={styles.wrapper}>
      <div className={styles.header}>
        <h3>{t('common.integrations')}</h3>
        <p className={styles.subtitle}>
          Connect Shakkii with your favorite tools to streamline your workflow
        </p>
      </div>

      <Grid className={styles.integrationsGrid} stackable centered>
        {integrations.map(renderIntegrationTile)}
      </Grid>

      {/* Modal para configuración de integración */}
      <Modal
        open={!!selectedIntegration}
        onClose={handleCloseModal}
        size="small"
        className={styles.integrationModal}
      >
        {selectedIntegration && (
          <>
            <Modal.Header>
              <div className={styles.modalHeader}>
                {selectedIntegration.logo ? (
                  <img
                    src={selectedIntegration.logo}
                    alt={`${selectedIntegration.name} logo`}
                    className={styles.modalLogo}
                  />
                ) : (
                  <div
                    className={styles.modalIcon}
                    style={{ backgroundColor: selectedIntegration.color }}
                  >
                    {selectedIntegration.icon}
                  </div>
                )}
                <span>
                  {selectedIntegration.name}
                  {!selectedIntegration.name.toLowerCase().includes('integration') &&
                    ' Integration'}
                </span>
              </div>
            </Modal.Header>
            <Modal.Content>
              {selectedIntegration.id === 'wheelbase' ? (
                <div className={styles.wheelbaseForm}>
                  {wheelbaseConfig ? (
                    // Configuration exists - show sync interface
                    <div>
                      <div className={styles.configuredHeader}>
                        <Icon name="check circle" color="green" />
                        <div>
                          <h4>Wheelbase Connected</h4>
                          <p>Email: {wheelbaseConfig.wheelbaseEmail}</p>
                          <p>Owner ID: {wheelbaseConfig.wheelbaseOwnerId}</p>
                          <p>
                            Last sync:{' '}
                            {wheelbaseConfig.lastSyncAt
                              ? new Date(wheelbaseConfig.lastSyncAt).toLocaleString()
                              : 'Never'}
                          </p>
                        </div>
                      </div>

                      {/* Customer Details Progress - DISABLED (se queda pegado en 0%) */}
                      {false &&
                        customerDetailsProgress &&
                        customerDetailsProgress.status === 'running' && (
                          <div className={styles.progressSection}>
                            <div className={styles.progressHeader}>
                              <Icon name="sync" loading color="blue" />
                              <span>
                                Syncing customer profiles:{' '}
                                {customerDetailsProgress.processedRecords || 0}/
                                {customerDetailsProgress.totalRecords || 0} (
                                {customerDetailsProgress.percentage || 0}%)
                              </span>
                            </div>
                            <div className={styles.progressBarContainer}>
                              <div
                                className={styles.progressBar}
                                style={{ width: `${customerDetailsProgress.percentage || 0}%` }}
                              />
                            </div>
                            {customerDetailsProgress.estimatedCompletion && (
                              <div className={styles.progressEta}>
                                <small>
                                  Estimated completion:{' '}
                                  {new Date(
                                    customerDetailsProgress.estimatedCompletion
                                  ).toLocaleTimeString()}
                                </small>
                              </div>
                            )}
                          </div>
                        )}

                      {/* Vehicle Details Progress - DISABLED (se queda pegado en 0%) */}
                      {false &&
                        vehicleDetailsProgress &&
                        vehicleDetailsProgress.status === 'running' && (
                          <div className={styles.progressSection}>
                            <div className={styles.progressHeader}>
                              <Icon name="sync" loading color="blue" />
                              <span>
                                Syncing vehicle details:{' '}
                                {vehicleDetailsProgress.processedRecords || 0}/
                                {vehicleDetailsProgress.totalRecords || 0} (
                                {vehicleDetailsProgress.percentage || 0}%)
                              </span>
                            </div>
                            <div className={styles.progressBarContainer}>
                              <div
                                className={styles.progressBar}
                                style={{ width: `${vehicleDetailsProgress.percentage || 0}%` }}
                              />
                            </div>
                            {vehicleDetailsProgress.estimatedCompletion && (
                              <div className={styles.progressEta}>
                                <small>
                                  Estimated completion:{' '}
                                  {new Date(
                                    vehicleDetailsProgress.estimatedCompletion
                                  ).toLocaleTimeString()}
                                </small>
                              </div>
                            )}
                          </div>
                        )}

                      {/* Booking Details Progress - DISABLED (se queda pegado en 0%) */}
                      {false &&
                        bookingDetailsProgress &&
                        bookingDetailsProgress.status === 'running' && (
                          <div className={styles.progressSection}>
                            <div className={styles.progressHeader}>
                              <Icon name="sync" loading color="blue" />
                              <span>
                                Syncing booking details:{' '}
                                {bookingDetailsProgress.processedRecords || 0}/
                                {bookingDetailsProgress.totalRecords || 0} (
                                {bookingDetailsProgress.percentage || 0}%)
                              </span>
                            </div>
                            <div className={styles.progressBarContainer}>
                              <div
                                className={styles.progressBar}
                                style={{ width: `${bookingDetailsProgress.percentage || 0}%` }}
                              />
                            </div>
                            {bookingDetailsProgress.estimatedCompletion && (
                              <div className={styles.progressEta}>
                                <small>
                                  Estimated completion:{' '}
                                  {new Date(
                                    bookingDetailsProgress.estimatedCompletion
                                  ).toLocaleTimeString()}
                                </small>
                              </div>
                            )}
                          </div>
                        )}

                      <div className={styles.syncActions}>
                        <Button
                          primary
                          loading={isSyncing}
                          disabled={isSyncing || isSyncingIncremental || isCreatingWeeklyCards}
                          onClick={handleSyncData}
                          icon="refresh"
                          content="Sync All Data"
                        />
                        <Button
                          color="blue"
                          loading={isSyncingIncremental}
                          disabled={isSyncing || isSyncingIncremental || isCreatingWeeklyCards}
                          onClick={handleSyncIncremental}
                          icon="sync alternate"
                          content="Sync Changes Only"
                        />
                        <Button
                          color="green"
                          loading={isCreatingWeeklyCards}
                          disabled={isSyncing || isSyncingIncremental || isCreatingWeeklyCards}
                          onClick={handleCreateWeeklyCards}
                          icon="calendar check"
                          content="Create Weekly Cards"
                        />
                        <Button
                          negative
                          disabled={
                            isLoading || isSyncing || isSyncingIncremental || isCreatingWeeklyCards
                          }
                          onClick={handleDeleteIntegration}
                          icon="trash"
                          content="Delete Integration"
                        />
                      </div>
                    </div>
                  ) : (
                    // No configuration - show setup form
                    <div>
                      <p className={styles.formDescription}>
                        Enter your Wheelbase credentials to connect your fleet management data with
                        Shakkii. Your credentials are encrypted and stored securely.
                      </p>

                      <Form>
                        <Form.Field>
                          <label>Wheelbase Email</label>
                          <input
                            type="email"
                            placeholder="your-email@company.com"
                            value={wheelbaseEmail || ''}
                            onChange={e => setWheelbaseEmail(e.target.value)}
                            disabled={isLoading}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            name="wheelbase-email-unique"
                            style={{
                              width: '100%',
                              padding: '0.67861429em 1em',
                              fontSize: '1em',
                              border: '1px solid rgba(34,36,38,.15)',
                              borderRadius: '.28571429rem',
                            }}
                          />
                        </Form.Field>

                        <Form.Field>
                          <label>Wheelbase Password</label>
                          <input
                            type="password"
                            placeholder="Enter your password"
                            value={wheelbasePassword || ''}
                            onChange={e => setWheelbasePassword(e.target.value)}
                            disabled={isLoading}
                            autoComplete="new-password"
                            name="wheelbase-password-unique"
                            style={{
                              width: '100%',
                              padding: '0.67861429em 1em',
                              fontSize: '1em',
                              border: '1px solid rgba(34,36,38,.15)',
                              borderRadius: '.28571429rem',
                            }}
                          />
                        </Form.Field>
                      </Form>
                    </div>
                  )}

                  {error && (
                    <Message negative>
                      <Message.Header>Error</Message.Header>
                      <p>{typeof error === 'string' ? error : JSON.stringify(error)}</p>
                    </Message>
                  )}

                  {success && (
                    <Message positive>
                      <Message.Header>Success</Message.Header>
                      <p>{typeof success === 'string' ? success : JSON.stringify(success)}</p>
                    </Message>
                  )}
                </div>
              ) : selectedIntegration.id === 'slack' ? (
                <div className={styles.slackForm}>
                  {slackConfig && (
                    <>
                      <div className={styles.configuredHeader}>
                        <Icon name="check circle" color="green" />
                        <div>
                          <h4>Slack Connected</h4>
                          <p>Workspace: {slackConfig.teamName}</p>
                          <p>Team ID: {slackConfig.teamId}</p>
                          {slackConfig.channelId && (
                            <p>Default Channel ID: {slackConfig.channelId}</p>
                          )}
                          {slackConfig.channels?.length > 0 && (
                            <div className={styles.channelSummary}>
                              <strong>Channels:</strong>
                              <ul>
                                {slackConfig.channels.map(channel => (
                                  <li key={channel.id}>
                                    {channel.name ? `${channel.name} (${channel.id})` : channel.id}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>

                      {testChannelOptions.length > 0 && (
                        <div className={styles.slackTestCard}>
                          <h5>Send Test Message</h5>
                          <Form>
                            <Form.Field>
                              <label>Channel</label>
                              <Form.Select
                                placeholder="Select Slack channel"
                                options={testChannelOptions}
                                value={selectedTestChannelId || testChannelOptions[0]?.value || ''}
                                onChange={(e, { value }) => setSelectedTestChannelId(value)}
                                disabled={isTesting || !testChannelOptions.length}
                                className={styles.selectField}
                              />
                            </Form.Field>
                            <Form.Field>
                              <label>Message</label>
                              <Form.TextArea
                                rows={3}
                                placeholder="Type the message you want to send as a test"
                                value={customTestMessage}
                                onChange={e => setCustomTestMessage(e.target.value)}
                                disabled={isTesting}
                                style={{
                                  backgroundColor: '#fff',
                                  border: '1px solid rgba(34,36,38,.15)',
                                }}
                              />
                            </Form.Field>
                            <Button
                              primary
                              loading={isTesting}
                              disabled={isTesting || !testChannelOptions.length}
                              onClick={handleTestSlack}
                              icon="paper plane"
                              content="Send Test Message"
                            />
                          </Form>
                        </div>
                      )}

                      <div className={styles.syncActions}>
                        <Button
                          negative
                          disabled={isLoading || isTesting}
                          onClick={handleDisconnectSlack}
                          icon="unlink"
                          content="Disconnect Slack"
                        />
                      </div>
                    </>
                  )}

                  {!slackConfig && (
                    <p className={styles.formDescription}>
                      Enter your Slack Bot Token and the channels that should receive notifications.
                      Get your credentials from{' '}
                      <a
                        href="https://api.slack.com/apps"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Slack API
                      </a>
                      . Your credentials are encrypted and stored securely.
                    </p>
                  )}

                  <Form className={styles.slackConfigForm}>
                    <Form.Field>
                      <label>Slack Bot Token</label>
                      <input
                        type="text"
                        placeholder="xoxb-your-bot-token"
                        value={slackBotToken || ''}
                        onChange={e => setSlackBotToken(e.target.value)}
                        disabled={isLoading}
                        autoComplete="new-password"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        name="slack-bot-token-unique-field"
                        style={{
                          width: '100%',
                          padding: '0.67861429em 1em',
                          fontSize: '1em',
                          border: '1px solid rgba(34,36,38,.15)',
                          borderRadius: '.28571429rem',
                          backgroundColor: '#fff',
                        }}
                      />
                      <span className={styles.helpText}>
                        Required when connecting for the first time or when rotating your Slack
                        token.
                      </span>
                    </Form.Field>

                    <Form.Field>
                      <label>Default test message</label>
                      <Form.TextArea
                        rows={3}
                        placeholder="🧪 Slack Integration Test..."
                        value={defaultSlackTestMessage}
                        onChange={e => setDefaultSlackTestMessage(e.target.value)}
                        disabled={isLoading}
                        style={{
                          backgroundColor: '#fff',
                          border: '1px solid rgba(34,36,38,.15)',
                        }}
                      />
                    </Form.Field>

                    <Form.Field>
                      <label>Slack Channels</label>
                      <span className={styles.helpText}>
                        Add all channel IDs (format C...) that should receive notifications.
                      </span>
                    </Form.Field>

                    <div className={styles.channelList}>
                      {slackChannels.map((channel, index) => (
                        <div key={`slack-channel-${index}`} className={styles.channelRow}>
                          <input
                            type="text"
                            placeholder="Channel ID (e.g. C01234ABCDE)"
                            value={channel.id || ''}
                            onChange={e => handleSlackChannelChange(index, 'id', e.target.value)}
                            disabled={isLoading}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            style={{
                              width: '100%',
                              padding: '0.67861429em 1em',
                              fontSize: '1em',
                              border: '1px solid rgba(34,36,38,.15)',
                              borderRadius: '.28571429rem',
                              backgroundColor: '#fff',
                            }}
                          />
                          <input
                            type="text"
                            placeholder="Friendly name (optional)"
                            value={channel.name || ''}
                            onChange={e => handleSlackChannelChange(index, 'name', e.target.value)}
                            disabled={isLoading}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            style={{
                              width: '100%',
                              padding: '0.67861429em 1em',
                              fontSize: '1em',
                              border: '1px solid rgba(34,36,38,.15)',
                              borderRadius: '.28571429rem',
                              backgroundColor: '#fff',
                            }}
                          />
                          <Button
                            type="button"
                            icon="trash"
                            basic
                            disabled={isLoading}
                            onClick={() => handleRemoveSlackChannel(index)}
                          />
                        </div>
                      ))}
                    </div>

                    <Button
                      type="button"
                      basic
                      icon
                      labelPosition="left"
                      onClick={handleAddSlackChannel}
                      disabled={isLoading}
                      className={styles.addChannelButton}
                    >
                      <Icon name="plus" />
                      Add Channel
                    </Button>
                  </Form>

                  {error && (
                    <Message negative>
                      <Message.Header>Error</Message.Header>
                      <p>{typeof error === 'string' ? error : JSON.stringify(error)}</p>
                    </Message>
                  )}

                  {success && (
                    <Message positive>
                      <Message.Header>Success</Message.Header>
                      <p>{typeof success === 'string' ? success : JSON.stringify(success)}</p>
                    </Message>
                  )}
                </div>
              ) : selectedIntegration.id === 'google-drive' ? (
                <div className={styles.googleDriveForm}>
                  {googleDriveConfig ? (
                    <>
                      <div className={styles.configuredHeader}>
                        <Icon name="check circle" color="green" />
                        <div>
                          <h4>Google Drive Connected</h4>
                          <p>Account: {googleDriveConfig.email}</p>
                          <p>Name: {googleDriveConfig.name}</p>
                          <p>
                            Connected since:{' '}
                            {new Date(googleDriveConfig.connectedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: '20px',
                          marginBottom: '20px',
                          borderTop: '1px solid #eee',
                          paddingTop: '15px',
                          opacity: 0.5,
                        }}
                      >
                        <h4>Knowledge Base (Moved to Project Settings)</h4>
                        <p style={{ color: '#666', fontSize: '0.9em' }}>
                          The Knowledge Base has been moved to project-level settings. Go to{' '}
                          <strong>Project Settings &gt; AI Agent Configuration</strong> to set a
                          canon document for each project.
                        </p>
                        <p style={{ color: '#999', fontSize: '0.85em', fontStyle: 'italic' }}>
                          Documents are now refreshed only when a project is opened, saving tokens
                          and cost.
                        </p>
                      </div>

                      <div className={styles.syncActions}>
                        <Button
                          negative
                          disabled={isLoading}
                          onClick={handleDisconnectGoogle}
                          icon="unlink"
                          content="Disconnect Google Drive"
                        />
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <p className={styles.formDescription}>
                        Connect your Google Drive to allow creating and accessing documents directly
                        from cards.
                      </p>
                      <Button
                        primary
                        size="large"
                        onClick={handleConnectGoogle}
                        loading={isLoading}
                        icon
                        labelPosition="left"
                      >
                        <Icon name="google" />
                        Connect with Google
                      </Button>
                    </div>
                  )}

                  {error && (
                    <Message negative>
                      <Message.Header>Error</Message.Header>
                      <p>{typeof error === 'string' ? error : JSON.stringify(error)}</p>
                    </Message>
                  )}

                  {success && (
                    <Message positive>
                      <Message.Header>Success</Message.Header>
                      <p>{typeof success === 'string' ? success : JSON.stringify(success)}</p>
                    </Message>
                  )}
                </div>
              ) : selectedIntegration.id === 'rentalbuddy' ? (
                <div className={styles.rentalbuddyForm}>
                  {isLoading && !rentalbuddyConfig ? (
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                      <Icon name="spinner" loading size="large" />
                      <p style={{ marginTop: '1rem', color: '#666' }}>
                        Loading integration settings...
                      </p>
                    </div>
                  ) : rentalbuddyConfig ? (
                    <div className={styles.formContainer}>
                      <Form size="large">
                        <div className={styles.section}>
                          <Form.Field>
                            <label className={styles.fieldLabel}>Integration Status</label>
                            <div style={{ display: 'flex', alignItems: 'center', height: '42px' }}>
                              <Form.Checkbox
                                toggle
                                size="large"
                                label={rentalbuddyConfig.isActive ? 'Enabled' : 'Disabled'}
                                checked={rentalbuddyConfig.isActive}
                                onChange={(e, { checked }) => handleUpdateRentalbuddy(checked)}
                                disabled={isLoading}
                                style={{ fontWeight: '600' }}
                              />
                            </div>
                          </Form.Field>
                        </div>

                        <Message
                          info
                          icon="info circle"
                          header="Configuration Required"
                          content="In the Rentalbuddy Dashboard, configure your integration with Shakkii using the following endpoints and security credentials."
                          style={{ marginBottom: '2rem' }}
                        />

                        <div className={styles.fieldGroup}>
                          <Form.Field>
                            <label className={styles.fieldLabel}>
                              Webhook URL — MT (Multi-Tenant)
                            </label>
                            <div className={styles.inputGroup}>
                              <div className={styles.inputWrapper}>
                                <input
                                  readOnly
                                  value={rentalbuddyConfig.webhookUrlMT || ''}
                                  className={styles.customInput}
                                  placeholder="Not available"
                                />
                              </div>
                              <Button
                                icon="copy"
                                basic
                                size="large"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    rentalbuddyConfig.webhookUrlMT || ''
                                  );
                                  setSuccess('MT URL copied to clipboard!');
                                }}
                                title="Copy MT URL"
                                className={styles.actionButton}
                                disabled={!rentalbuddyConfig.webhookUrlMT}
                              />
                            </div>
                          </Form.Field>

                          <Form.Field>
                            <label className={styles.fieldLabel}>Webhook URL — Legacy</label>
                            <div className={styles.inputGroup}>
                              <div className={styles.inputWrapper}>
                                <input
                                  readOnly
                                  value={rentalbuddyConfig.webhookUrl || ''}
                                  className={styles.customInput}
                                />
                              </div>
                              <Button
                                icon="copy"
                                basic
                                size="large"
                                onClick={() => {
                                  navigator.clipboard.writeText(rentalbuddyConfig.webhookUrl || '');
                                  setSuccess('Legacy URL copied to clipboard!');
                                }}
                                title="Copy Legacy URL"
                                className={styles.actionButton}
                              />
                            </div>
                          </Form.Field>

                          <Form.Field>
                            <label className={styles.fieldLabel}>Tenant ID</label>
                            <div className={styles.inputGroup}>
                              <div className={styles.inputWrapper}>
                                <input
                                  readOnly
                                  value={rentalbuddyConfig.tenantId}
                                  className={styles.customInput}
                                />
                              </div>
                              <Button
                                icon="copy"
                                basic
                                size="large"
                                onClick={() => {
                                  navigator.clipboard.writeText(rentalbuddyConfig.tenantId);
                                  setSuccess('Tenant ID copied to clipboard!');
                                }}
                                title="Copy to clipboard"
                                className={styles.actionButton}
                              />
                            </div>
                          </Form.Field>

                          <Form.Field>
                            <label className={styles.fieldLabel}>Webhook Secret (HMAC)</label>
                            <div className={styles.inputGroup}>
                              <div className={styles.inputWrapper}>
                                <input
                                  readOnly
                                  type={showFullSecret ? 'text' : 'password'}
                                  value={rentalbuddyConfig.webhookSecret}
                                  className={styles.customInput}
                                />
                              </div>
                              <Button
                                icon={showFullSecret ? 'eye slash' : 'eye'}
                                basic
                                size="large"
                                onClick={() => setShowFullSecret(!showFullSecret)}
                                title={showFullSecret ? 'Show/Hide secret' : 'Show secret'}
                                className={styles.actionButton}
                              />
                              <Button
                                icon="copy"
                                basic
                                size="large"
                                onClick={() => {
                                  navigator.clipboard.writeText(rentalbuddyConfig.webhookSecret);
                                  setSuccess('Secret copied to clipboard!');
                                }}
                                title="Copy to clipboard"
                                className={styles.actionButton}
                              />
                              <Button
                                icon="refresh"
                                loading={isRotatingSecret}
                                basic
                                size="large"
                                color="orange"
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      'Are you sure you want to rotate the secret? Current integration will stop working until you update it in Rentalbuddy.'
                                    )
                                  ) {
                                    handleUpdateRentalbuddy(rentalbuddyConfig.isActive, true);
                                  }
                                }}
                                title="Rotate secret"
                                className={styles.actionButton}
                              />
                            </div>
                            <small
                              style={{
                                display: 'block',
                                marginTop: '8px',
                                color: 'rgba(0,0,0,0.4)',
                                fontSize: '0.85rem',
                              }}
                            >
                              This secret is used to sign requests and verify they come from
                              Rentalbuddy.
                            </small>
                          </Form.Field>
                        </div>

                        <div className={styles.section} style={{ marginTop: '2.5rem' }}>
                          <h4 style={{ marginBottom: '1.5rem' }}>Data API</h4>
                          <Message
                            info
                            icon="database"
                            header="Owner ID (Firebase)"
                            content="The Firebase UID used to query booking data from the RentalBuddy Data API. You can find it in your Firebase console under Go360owners/."
                            style={{ marginBottom: '1.5rem' }}
                          />
                          <Form.Field>
                            <label className={styles.fieldLabel}>Owner ID (Firebase)</label>
                            <div className={styles.inputWrapper}>
                              <input
                                value={rentalbuddyFirebaseUid}
                                onChange={e => setRentalbuddyFirebaseUid(e.target.value)}
                                className={styles.customInput}
                                placeholder="e.g. rBjO0dGH1lex5UPh9zOZzdIsG2c2"
                                disabled={isSavingFirebaseUid}
                              />
                            </div>
                            <Button
                              primary
                              size="small"
                              content="Save"
                              icon="save"
                              onClick={handleSaveFirebaseUid}
                              loading={isSavingFirebaseUid}
                              disabled={
                                isSavingFirebaseUid ||
                                rentalbuddyFirebaseUid.trim() ===
                                  (rentalbuddyConfig?.firebaseUid || '')
                              }
                              style={{ marginTop: '0.5rem' }}
                            />
                          </Form.Field>
                        </div>

                        <div className={styles.section} style={{ marginTop: '2.5rem' }}>
                          <h4 style={{ marginBottom: '1.5rem' }}>Automated Board Setup</h4>
                          <Form.Field>
                            <label className={styles.fieldLabel}>Select Target Project</label>
                            <Form.Select
                              fluid
                              placeholder="Select Project"
                              options={projects.map(p => ({
                                key: p.id,
                                value: p.id,
                                text: p.name,
                              }))}
                              value={rentalbuddyTargetProjectId}
                              onChange={(e, { value }) => setRentalbuddyTargetProjectId(value)}
                              disabled={isSettingUpBoard}
                            />
                          </Form.Field>

                          <Form.Field style={{ marginTop: '1.5rem' }}>
                            <label className={styles.fieldLabel}>Enabled Steps (Lists)</label>
                            <div className={styles.stepsScrollContainer}>
                              {rentalbuddyAvailableSteps.map(group => (
                                <React.Fragment key={group.group}>
                                  <div className={styles.stepGroupHeader}>{group.group}</div>
                                  {group.steps.map(step => (
                                    <div key={step} className={styles.stepItem}>
                                      <Form.Checkbox
                                        label={step}
                                        checked={rentalbuddyEnabledSteps.includes(step)}
                                        onChange={() => toggleRentalbuddyStep(step)}
                                        disabled={isSettingUpBoard}
                                      />
                                    </div>
                                  ))}
                                </React.Fragment>
                              ))}
                            </div>
                          </Form.Field>

                          <Button
                            primary
                            fluid
                            size="large"
                            content="Create Rental Buddy Board"
                            icon="plus"
                            onClick={handleCreateRentalbuddyBoard}
                            loading={isSettingUpBoard}
                            disabled={isSettingUpBoard || !rentalbuddyTargetProjectId}
                            style={{ marginTop: '1rem' }}
                          />
                        </div>
                      </Form>

                      <div style={{ marginTop: '3rem' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1rem',
                          }}
                        >
                          <h4 style={{ margin: 0 }}>Recent Deliveries</h4>
                          <Button
                            basic
                            compact
                            size="tiny"
                            icon="refresh"
                            content="Refresh"
                            onClick={loadRentalbuddyDeliveries}
                            loading={isLoading}
                          />
                        </div>

                        {rentalbuddyDeliveries.length > 0 ? (
                          <div className={styles.deliveryTable}>
                            <table>
                              <thead>
                                <tr>
                                  <th>Date & Time</th>
                                  <th>Status</th>
                                  <th>Delivery ID</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rentalbuddyDeliveries.map(delivery => (
                                  <tr key={delivery.deliveryId}>
                                    <td>{new Date(delivery.receivedAt).toLocaleString()}</td>
                                    <td>
                                      <span
                                        className={`${styles.statusTag} ${styles[delivery.status]}`}
                                      >
                                        <Icon
                                          name={
                                            delivery.status === 'processed'
                                              ? 'check circle'
                                              : delivery.status === 'failed'
                                              ? 'times circle'
                                              : 'clock outline'
                                          }
                                        />
                                        {delivery.status.charAt(0).toUpperCase() +
                                          delivery.status.slice(1)}
                                      </span>
                                    </td>
                                    <td
                                      style={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.85em',
                                        color: '#666',
                                      }}
                                      title={delivery.deliveryId}
                                    >
                                      {delivery.deliveryId.length > 20
                                        ? `${delivery.deliveryId.substring(0, 20)}...`
                                        : delivery.deliveryId}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div
                            style={{
                              padding: '30px',
                              textAlign: 'center',
                              color: '#999',
                              backgroundColor: '#f9fafb',
                              borderRadius: '8px',
                              border: '1px dashed rgba(34,36,38,0.1)',
                            }}
                          >
                            No activity recorded yet.
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {error && (
                    <Message negative style={{ marginTop: '1.5rem' }}>
                      <Message.Header>Error</Message.Header>
                      <p>{typeof error === 'string' ? error : JSON.stringify(error)}</p>
                    </Message>
                  )}

                  {success && (
                    <Message positive style={{ marginTop: '1.5rem' }}>
                      <Message.Header>Success</Message.Header>
                      <p>{typeof success === 'string' ? success : JSON.stringify(success)}</p>
                    </Message>
                  )}
                </div>
              ) : selectedIntegration.id === 'rcm_vehicles' ? (
                <div className={styles.rcmForm}>
                  {rcmVehiclesConfig ? (
                    <div>
                      <div className={styles.configuredHeader}>
                        <Icon name="check circle" color="green" />
                        <div>
                          <h4>RCM Vehicles Connected</h4>
                          <p>Authentication: API Key (server environment variable)</p>
                          <p>Default Location ID: {rcmVehiclesConfig.currentLocationId}</p>
                          {rcmVehiclesConfig.rcmCompanyId && (
                            <p>Company ID: {rcmVehiclesConfig.rcmCompanyId}</p>
                          )}
                          <p>
                            Last updated:{' '}
                            {rcmVehiclesConfig.updatedAt
                              ? new Date(rcmVehiclesConfig.updatedAt).toLocaleString()
                              : 'Never'}
                          </p>
                        </div>
                      </div>

                      <div className={styles.syncActions} style={{ marginTop: '20px' }}>
                        <Button
                          primary
                          loading={isSyncing}
                          disabled={isSyncing || isLoading}
                          onClick={handleRcmSyncFleet}
                          icon="refresh"
                          content="Sync Fleet"
                        />
                        <Button
                          negative
                          disabled={isLoading || isSyncing}
                          onClick={() => handleRcmDelete('rcm_vehicles')}
                          icon="trash"
                          content="Delete Integration"
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className={styles.formDescription}>
                        Configure RCM Vehicles integration settings. Authentication is handled via
                        the RCM_API_KEY environment variable on the server.
                      </p>

                      <Form>
                        <Form.Field>
                          <label>Default Location ID</label>
                          <input
                            type="number"
                            placeholder="7"
                            value={rcmLocationId}
                            onChange={e => setRcmLocationId(parseInt(e.target.value, 10) || 0)}
                            disabled={isLoading}
                            name="rcm-location-id-unique"
                            style={{
                              width: '100%',
                              padding: '0.67861429em 1em',
                              fontSize: '1em',
                              border: '1px solid rgba(34,36,38,.15)',
                              borderRadius: '.28571429rem',
                            }}
                          />
                          <span className={styles.helpText}>
                            The location ID used by default when updating vehicle status.
                          </span>
                        </Form.Field>

                        <Form.Field>
                          <label>Company ID (Optional)</label>
                          <input
                            type="text"
                            placeholder="Enter Company ID for multi-tenant setup"
                            value={rcmCompanyId || ''}
                            onChange={e => setRcmCompanyId(e.target.value)}
                            disabled={isLoading}
                            name="rcm-company-id-unique"
                            style={{
                              width: '100%',
                              padding: '0.67861429em 1em',
                              fontSize: '1em',
                              border: '1px solid rgba(34,36,38,.15)',
                              borderRadius: '.28571429rem',
                            }}
                          />
                          <span className={styles.helpText}>
                            Required for multi-company RCM accounts. Leave empty if not applicable.
                          </span>
                        </Form.Field>

                        <Button
                          primary
                          fluid
                          size="large"
                          onClick={() => handleRcmConfigure('rcm_vehicles')}
                          loading={isLoading}
                          disabled={isLoading}
                          icon="save"
                          content="Save Vehicles Config"
                        />
                      </Form>
                    </div>
                  )}

                  {error && (
                    <Message negative style={{ marginTop: '15px' }}>
                      <Message.Header>Error</Message.Header>
                      <p>{typeof error === 'string' ? error : JSON.stringify(error)}</p>
                    </Message>
                  )}

                  {success && (
                    <Message positive style={{ marginTop: '15px' }}>
                      <Message.Header>Success</Message.Header>
                      <p>{typeof success === 'string' ? success : JSON.stringify(success)}</p>
                    </Message>
                  )}
                </div>
              ) : selectedIntegration.id === 'rcm_bookings' ? (
                <div className={styles.rcmForm}>
                  {rcmBookingsConfig ? (
                    <div>
                      <div className={styles.configuredHeader}>
                        <Icon name="check circle" color="green" />
                        <div>
                          <h4>RCM Bookings Connected</h4>
                          <p style={{ wordBreak: 'break-all' }}>
                            API Key: {rcmBookingsConfig.username}
                          </p>
                          <p>
                            Last updated:{' '}
                            {rcmBookingsConfig.updatedAt
                              ? new Date(rcmBookingsConfig.updatedAt).toLocaleString()
                              : 'Never'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className={styles.formDescription}>
                        Enter your Rental Car Manager (RCM) Export API credentials to enable booking
                        sync features.
                      </p>

                      <Form>
                        <Form.Field>
                          <label>RCM API Key</label>
                          <input
                            type="text"
                            placeholder="Enter API Key"
                            value={rcmBookingsApiKey || ''}
                            onChange={e => setRcmBookingsApiKey(e.target.value)}
                            disabled={isLoading}
                            autoComplete="off"
                            name="rcm-apikey-unique"
                            style={{
                              width: '100%',
                              padding: '0.67861429em 1em',
                              fontSize: '1em',
                              border: '1px solid rgba(34,36,38,.15)',
                              borderRadius: '.28571429rem',
                            }}
                          />
                        </Form.Field>

                        <Form.Field>
                          <label>Shared Secret</label>
                          <input
                            type="password"
                            placeholder="Enter Shared Secret"
                            value={rcmBookingsSecret || ''}
                            onChange={e => setRcmBookingsSecret(e.target.value)}
                            disabled={isLoading}
                            autoComplete="new-password"
                            name="rcm-secret-unique"
                            style={{
                              width: '100%',
                              padding: '0.67861429em 1em',
                              fontSize: '1em',
                              border: '1px solid rgba(34,36,38,.15)',
                              borderRadius: '.28571429rem',
                            }}
                          />
                        </Form.Field>

                        <Button
                          primary
                          fluid
                          size="large"
                          onClick={() => handleRcmConfigure('rcm_bookings')}
                          loading={isLoading}
                          disabled={
                            isLoading ||
                            ((!rcmBookingsApiKey.trim() || !rcmBookingsSecret.trim()) &&
                              !rcmBookingsConfig)
                          }
                          icon="save"
                          content="Save Bookings Config"
                        />
                      </Form>
                    </div>
                  )}

                  {rcmBookingsConfig && (
                    <div
                      style={{
                        marginTop: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                      }}
                    >
                      <RcmWebhookConfig
                        config={rcmBookingsConfig}
                        accessToken={accessToken}
                        serverBaseUrl={Config.SERVER_BASE_URL}
                        onConfigUpdate={handleRcmBookingsConfigUpdate}
                        isLoading={isLoading}
                      />

                      <RcmSyncConfig
                        config={rcmBookingsConfig}
                        isLoading={isLoading}
                        onConfigUpdate={handleRcmBookingsConfigUpdate}
                        onAutoSyncNow={handleRcmAutoSyncNow}
                        isAutoSyncing={isAutoSyncing}
                        accessToken={accessToken}
                      />

                      <RcmEnrichmentToggles
                        config={rcmBookingsConfig}
                        isLoading={isLoading}
                        onConfigUpdate={handleRcmBookingsConfigUpdate}
                      />

                      <RcmDamageConfig
                        config={rcmBookingsConfig}
                        accessToken={accessToken}
                        isLoading={isLoading}
                        onConfigUpdate={handleRcmBookingsConfigUpdate}
                      />

                      <RcmActivityLog
                        accessToken={accessToken}
                        serverBaseUrl={Config.SERVER_BASE_URL}
                      />
                    </div>
                  )}

                  {error && (
                    <Message negative style={{ marginTop: '15px' }}>
                      <Message.Header>Error</Message.Header>
                      <p>{typeof error === 'string' ? error : JSON.stringify(error)}</p>
                    </Message>
                  )}

                  {success && (
                    <Message positive style={{ marginTop: '15px' }}>
                      <Message.Header>Success</Message.Header>
                      <p>{typeof success === 'string' ? success : JSON.stringify(success)}</p>
                    </Message>
                  )}
                </div>
              ) : selectedIntegration.id === 'your-integration' ? (
                <div className={styles.yourIntegrationContent}>
                  <p className={styles.yourIntegrationText}>
                    Turn your idea into an integration. Just drop your app link, your API key, and
                    what you'd like it to do — our agent and community will take care of the rest,
                    helping you go live and start earning from your creation.
                  </p>
                </div>
              ) : (
                <div className={styles.comingSoonContent}>
                  <p>{selectedIntegration.description}</p>
                  <p>
                    <strong>Status:</strong> Coming soon - Integration in development
                  </p>
                </div>
              )}
            </Modal.Content>
            <Modal.Actions>
              {selectedIntegration.id === 'wheelbase' ? (
                <>
                  <Button onClick={handleCloseModal} disabled={isLoading}>
                    Close
                  </Button>
                  {!wheelbaseConfig && (
                    <Button
                      primary
                      loading={isLoading}
                      disabled={!wheelbaseEmail.trim() || !wheelbasePassword.trim() || isLoading}
                      onClick={handleSaveCredentials}
                      icon="save"
                      content="Save Credentials"
                    />
                  )}
                </>
              ) : selectedIntegration.id === 'slack' ? (
                <>
                  <Button onClick={handleCloseModal} disabled={isLoading || isTesting}>
                    Close
                  </Button>
                  <Button
                    primary
                    loading={isLoading}
                    disabled={slackSaveDisabled}
                    onClick={handleSaveSlackCredentials}
                    icon="save"
                    content={slackConfig ? 'Update Slack Settings' : 'Connect Slack'}
                  />
                </>
              ) : selectedIntegration.id === 'google-drive' ? (
                <Button onClick={handleCloseModal}>Close</Button>
              ) : selectedIntegration.id === 'rcm_vehicles' ||
                selectedIntegration.id === 'rcm_bookings' ? (
                <Button onClick={handleCloseModal} disabled={isLoading}>
                  Close
                </Button>
              ) : (
                <Button onClick={handleCloseModal}>Close</Button>
              )}
            </Modal.Actions>
          </>
        )}
      </Modal>

      {isGoogleDriveKbModalOpen && (
        <GoogleDriveModal
          onClose={() => setIsGoogleDriveKbModalOpen(false)}
          onSelect={handleSyncKb}
          allowMultiple={true}
          forKnowledgeBase
        />
      )}
    </Tab.Pane>
  );
});

export default IntegrationsPane;
